import { create } from 'zustand';
import type { User } from 'firebase/auth';
import { signInWithPopup, signOut as firebaseSignOut, onAuthStateChanged } from 'firebase/auth';
import { doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { auth, googleProvider } from '../services/firebase';
import { db } from '../services/firebase';
import { useAiAccessStore } from './ai-access-store';

interface AuthState {
  user: User | null;
  loading: boolean;
  error: string | null;

  signInWithGoogle: (legalAccepted: boolean) => Promise<void>;
  signOut: () => Promise<void>;
  setUser: (user: User | null) => void;
}

export const useAuthStore = create<AuthState>()((set) => ({
  user: null,
  loading: true,
  error: null,

  signInWithGoogle: async (legalAccepted) => {
    if (!legalAccepted) {
      set({ error: 'You must accept the Terms of Service and Privacy Policy to continue.' });
      return;
    }

    try {
      set({ error: null });
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;
      await setDoc(
        doc(db, 'users', user.uid),
        {
          uid: user.uid,
          email: user.email ?? null,
          displayName: user.displayName ?? null,
          photoURL: user.photoURL ?? null,
          createdAt: serverTimestamp(),
          lastLogin: serverTimestamp(),
          legalAccepted: true,
          legalAcceptedAt: serverTimestamp(),
          legalVersion: '2026-02',
          roles: ['user'],
        },
        { merge: true }
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to sign in';
      set({ error: message });
    }
  },

  signOut: async () => {
    try {
      await firebaseSignOut(auth);
      set({ user: null });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to sign out';
      set({ error: message });
    }
  },

  setUser: (user) => set({ user, loading: false }),
}));

// Set up auth state listener
onAuthStateChanged(auth, (user) => {
  useAuthStore.getState().setUser(user);
  if (user) {
    void useAiAccessStore.getState().refreshStatus();
  } else {
    useAiAccessStore.getState().reset();
  }
});
